/**
 * Contract for writing a shader from a prompt.
 *
 * A user describes an effect; a model they chose writes the code; the code is
 * checked in the real runtime before it replaces what was running. The node,
 * the settings panel and the generation engine all code against these types.
 */

import type { ExtractedParam, RuntimeMode } from '../types'
import type { ShaderInputType } from '../media/contract'

// ---------------------------------------------------------------------------
// Providers — the user chooses who writes the shader
// ---------------------------------------------------------------------------

export const SHADER_PROVIDER_IDS = ['openrouter', 'grok', 'local'] as const
export type ShaderProviderId = (typeof SHADER_PROVIDER_IDS)[number]

export const SHADER_PROVIDER_LABELS: Record<ShaderProviderId, string> = {
  openrouter: 'Magic Lamp',
  grok: 'Grok account',
  local: 'On this device',
}

/**
 * How an OpenRouter request is paid for.
 * - `genie-credits`: through Genie's API; the wallet is charged before the stream starts.
 * - `own-key`: straight to openrouter.ai with the user's key; Genie is not involved.
 */
export type OpenRouterBilling = 'genie-credits' | 'own-key'

/** Non-secret provider choice. Safe to store on a node and in page JSON. */
export interface ShaderProviderConfig {
  provider: ShaderProviderId
  model: string
  /** OpenRouter only. */
  billing?: OpenRouterBilling
  /** Local only. Defaults to `DEFAULT_LOCAL_BASE_URL`. */
  baseUrl?: string
}

export const DEFAULT_LOCAL_BASE_URL = 'http://localhost:11434'

/**
 * API keys the user pasted. These stay in the user's own browser storage and
 * are sent only to the provider they belong to — never to Genie, never into
 * node state, page JSON or exported projects.
 */
export interface ShaderProviderSecrets {
  openrouter?: string
  grok?: string
}

// ---------------------------------------------------------------------------
// Request / result
// ---------------------------------------------------------------------------

/**
 * `create` writes a new shader. `edit` sends the current code as context so a
 * follow-up like "make it slower" changes what is there instead of starting over.
 */
export type ShaderGenerationMode = 'create' | 'edit'

export interface ShaderGenerationRequest {
  prompt: string
  mode: ShaderGenerationMode
  /** Required for `edit`. */
  current?: {
    code: string
    runtimeMode: RuntimeMode
    inputTypes: ShaderInputType[]
  }
  /**
   * Media connected to the node right now. The shader should use them — this is
   * what makes "whatever the user writes interacts with the plugged-in media" work.
   */
  connectedInputs: ShaderInputType[]
  /** A hint when creating; the model may pick another runtime if the effect needs it. */
  preferredRuntime?: RuntimeMode
}

export interface ShaderGenerationResult {
  code: string
  runtimeMode: RuntimeMode
  /** Inputs the code reads. The node grows a handle for each. */
  inputTypes: ShaderInputType[]
  /** Short node title, e.g. "Liquid Chrome". */
  title: string
  /** One sentence: what was made, or what changed. */
  summary: string
  /** Parsed from the code's `// @param` annotations. */
  params: ExtractedParam[]
}

// ---------------------------------------------------------------------------
// Progress and errors
// ---------------------------------------------------------------------------

export type ShaderGenerationPhase = 'generating' | 'validating' | 'repairing'

export interface ShaderGenerationProgress {
  phase: ShaderGenerationPhase
  /** Characters of model output received so far while streaming. */
  received?: number
  /**
   * Genie credits only: paid requests this run has made so far, counting the
   * one in progress. 2 during `repairing` means the repair is a second charge.
   */
  charges?: number
}

/**
 * Why a generation failed, so the UI can offer the right way out.
 * - `PRICING_NOT_FOUND` / `INSUFFICIENT_BALANCE`: Genie credits path refused before spending.
 * - `AUTH`: a missing or rejected key.
 * - `CORS`: a local model the browser is not allowed to reach (hosted page, no `OLLAMA_ORIGINS`).
 * - `VALIDATION`: the model's code would not compile or run, even after one repair attempt.
 * - `EMPTY`: the model answered with no usable code.
 * - `TRUNCATED`: the model ran out of room (output tokens or context window)
 *   before the program was finished. Not repaired: a retry would hit the same wall.
 */
export type ShaderGenerationErrorCode =
  | 'PRICING_NOT_FOUND'
  | 'INSUFFICIENT_BALANCE'
  | 'AUTH'
  | 'CORS'
  | 'NETWORK'
  | 'RATE_LIMITED'
  | 'VALIDATION'
  | 'EMPTY'
  | 'TRUNCATED'
  | 'ABORTED'
  | 'UNKNOWN'

export class ShaderGenerationError extends Error {
  readonly code: ShaderGenerationErrorCode
  /** Genie credits only: paid requests the failed run made (2 when a repair was charged too). */
  readonly charges?: number

  constructor(message: string, code: ShaderGenerationErrorCode, details: { charges?: number } = {}) {
    super(message)
    this.name = 'ShaderGenerationError'
    this.code = code
    if (details.charges !== undefined) this.charges = details.charges
  }
}

/** Outcome of running candidate code in a hidden copy of the real runtime. */
export type ShaderValidationResult =
  | { ok: true }
  | { ok: false; message: string; line?: number; kind?: string }

/** `signal` cancels a check in progress (the sandbox is torn down) when the generation is cancelled. */
export type ShaderValidator = (candidate: { code: string; runtimeMode: RuntimeMode; signal?: AbortSignal }) => Promise<ShaderValidationResult>

// ---------------------------------------------------------------------------
// Node state
// ---------------------------------------------------------------------------

/**
 * Shader node state fields added for AI authoring. Existing fields (`code`,
 * `runtimeMode`, `sketchId`, `extractedParams`, `paramValues`, `title`,
 * `previewPaused`) keep their meaning.
 */
export interface ShaderAuthoringState {
  /** Text in the composer, kept so it survives reselecting the node. */
  prompt?: string
  /**
   * Inputs the current code reads. Set by generation; for library sketches the
   * sketch's own `inputTypes` apply. Drives which handles the node shows.
   */
  inputTypes?: ShaderInputType[]
  /** Provider choice for this node. Falls back to the user's default. */
  provider?: ShaderProviderConfig
  /** Last generation's outcome. Never holds secrets. */
  generation?: {
    status: 'idle' | 'running' | 'error'
    error?: string
    code?: ShaderGenerationErrorCode
    summary?: string
  }
  /** Prompts applied to this node, oldest first. */
  promptHistory?: ShaderPromptEntry[]
}

export interface ShaderPromptEntry {
  id: string
  prompt: string
  mode: ShaderGenerationMode
  provider: ShaderProviderId
  model: string
  summary: string
  /** The title this generation proposed. An edit renames the node only while it still carries this. */
  title?: string
  /** When the prompt was submitted. Stays on this node; it is not a shared log. */
  at?: string
  /** Generated code, so this shader can restore or compare that revision. */
  code?: string
  runtimeMode?: RuntimeMode
  params?: ExtractedParam[]
  paramValues?: Record<string, number | string | boolean>
  /** Set when the prompt failed and the running code was left unchanged. */
  error?: string
}

// ---------------------------------------------------------------------------
// Host capability — the desktop app can serve some providers itself
// ---------------------------------------------------------------------------

export interface ShaderChatMessage {
  role: 'user' | 'assistant'
  content: string
}

/**
 * Optional. A host such as the desktop app can answer for providers it already
 * has a connection to (a signed-in Grok account, a local model behind its
 * engine), so the user does not paste a key into the node editor.
 *
 * `complete` may reject with any Error. When its `code` is a
 * `ShaderGenerationErrorCode` (e.g. `'AUTH'` for a missing sign-in) the node
 * offers the matching fix; otherwise the failure is `UNKNOWN`.
 */
export interface ShaderCodeModelHost {
  /** Providers this host can serve without a browser-held key. */
  providers: ShaderProviderId[]
  complete(request: {
    provider: ShaderProviderId
    model: string
    system: string
    messages: ShaderChatMessage[]
    signal: AbortSignal
    onText?: (textSoFar: string) => void
    temperature?: number
    maxTokens?: number
  }): Promise<string>
}
